import type { ManiaLane } from '@shared/maniaNote';
import type { PianoLaneFlash } from '../../piano/render/pianoRenderTypes';
import type { RenderQuality } from '../../rendering/renderQuality';
import { withAlpha } from '../../piano/render/pianoRenderColour';
import type { GuitarLayout } from '../guitarPlayfieldGeometry';
import { getLaneStrumCentre } from './guitarDrawScene';
import {
  LIGHTNING_BOLT_LIFE_S,
  LIGHTNING_BOLT_THICKNESS_PX,
  LIGHTNING_COLOUR,
  LIGHTNING_COMBO_THRESHOLD,
  LIGHTNING_COOLDOWN_S,
  LIGHTNING_JAG_AMPLITUDE_PX,
  LIGHTNING_JAG_SEGMENTS,
  LIGHTNING_POOL_SIZE,
} from './guitarRenderConstants';

interface Bolt {
  active: boolean;
  startX: number;
  endX: number;
  y: number;
  life: number;
  maxLife: number;
  /** Pre-allocated jag offsets in CSS px (length always = LIGHTNING_JAG_SEGMENTS). */
  jags: Float32Array;
}

/**
 * Guitar-specific FX layer that sits on top of {@link
 * import('../../piano/render/pianoFx').PianoFxSystem}. Currently owns
 * one effect — strum-bar lightning bolts — but is shaped as a class
 * so future Guitar-only juice (e.g. fret-cap arcs, beat-locked back-
 * wall pulses) can join the same pool-backed lifecycle.
 *
 * The detection contract: each frame, the renderer hands us the
 * current set of live lane flashes from the shared FX system. The
 * boost layer tracks which flashes it has already observed via a
 * {@link WeakSet}; any flashes new this frame are tagged as "fresh."
 * Two or more fresh flashes in the same frame === a chord landed, so
 * we spawn a bolt between the outermost lanes.
 *
 * Pool-backed so a sustained chord stream doesn't churn the GC. Eight
 * bolts is plenty — the cooldown gate ensures we never spawn more
 * than ~10 per second.
 */
export class GuitarFxBoost {
  private bolts: Bolt[] = [];
  private seenFlashes = new WeakSet<PianoLaneFlash>();
  private cooldown = 0;
  private reducedMotion: boolean;
  private quality: RenderQuality;

  constructor(reducedMotion: boolean, quality: RenderQuality) {
    this.reducedMotion = reducedMotion;
    this.quality = quality;
    for (let i = 0; i < LIGHTNING_POOL_SIZE; i += 1) {
      this.bolts.push({
        active: false,
        startX: 0,
        endX: 0,
        y: 0,
        life: 0,
        maxLife: 0,
        jags: new Float32Array(LIGHTNING_JAG_SEGMENTS),
      });
    }
  }

  clear(): void {
    for (const bolt of this.bolts) {
      bolt.active = false;
    }
    this.cooldown = 0;
  }

  /**
   * Detect new chord arrivals across the live lane-flash set and
   * advance any active bolt lifetimes. Called once per frame from
   * the renderer's draw loop, before {@link drawLightning}.
   *
   * On the low tier (or under reduced motion) lightning never spawns,
   * so we skip the WeakSet bookkeeping entirely — saves a per-flash
   * `has` + `add` on every frame of a sustained streak.
   */
  update(
    laneFlashes: ReadonlyArray<PianoLaneFlash>,
    canvasWidth: number,
    canvasHeight: number,
    combo: number,
    dt: number,
  ): void {
    if (dt > 0 && this.cooldown > 0) {
      this.cooldown = Math.max(0, this.cooldown - dt);
    }
    this.ageBolts(dt);

    if (this.reducedMotion || !this.quality.chromeShadows) {
      return;
    }

    let minLane: ManiaLane | -1 = -1;
    let maxLane: ManiaLane | -1 = -1;
    let freshCount = 0;
    for (const flash of laneFlashes) {
      if (this.seenFlashes.has(flash)) {
        continue;
      }
      this.seenFlashes.add(flash);
      freshCount += 1;
      if (minLane === -1 || flash.lane < minLane) {
        minLane = flash.lane;
      }
      if (maxLane === -1 || flash.lane > maxLane) {
        maxLane = flash.lane;
      }
    }

    if (
      combo < LIGHTNING_COMBO_THRESHOLD ||
      this.cooldown > 0 ||
      freshCount < 2 ||
      minLane === -1 ||
      maxLane === -1 ||
      minLane === maxLane
    ) {
      return;
    }
    this.spawnBolt(minLane, maxLane, canvasWidth, canvasHeight);
    this.cooldown = LIGHTNING_COOLDOWN_S;
  }

  /**
   * Render every active bolt as a jagged polyline across the strum
   * bar between the chord's outermost lane centres. Layered with
   * shadow glow on high-tier devices for a "electric arc" feel;
   * flat-stroke fallback on low tier (already gated out via the
   * spawn path, but the draw stays robust).
   */
  drawLightning(ctx: CanvasRenderingContext2D, layout: GuitarLayout): void {
    if (this.reducedMotion) {
      return;
    }
    let anyActive = false;
    for (const bolt of this.bolts) {
      if (bolt.active) {
        anyActive = true;
        break;
      }
    }
    if (!anyActive) {
      return;
    }
    ctx.save();
    for (const bolt of this.bolts) {
      if (!bolt.active) {
        continue;
      }
      const lifeFrac = Math.max(0, bolt.life / bolt.maxLife);
      const alpha = lifeFrac * lifeFrac;
      if (alpha < 0.02) {
        continue;
      }
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = LIGHTNING_COLOUR;
      ctx.lineWidth = LIGHTNING_BOLT_THICKNESS_PX * (0.6 + lifeFrac * 0.8);
      if (this.quality.chromeShadows) {
        ctx.shadowColor = LIGHTNING_COLOUR;
        ctx.shadowBlur = 14 * alpha;
      }
      this.tracePath(ctx, bolt, layout.strumBarY);
      ctx.stroke();
      ctx.strokeStyle = withAlpha('#ffffff', 0.85);
      ctx.lineWidth = Math.max(0.6, LIGHTNING_BOLT_THICKNESS_PX * 0.5 * lifeFrac);
      ctx.shadowBlur = 0;
      this.tracePath(ctx, bolt, layout.strumBarY);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    ctx.restore();
  }

  private ageBolts(dt: number): void {
    if (dt <= 0) {
      return;
    }
    for (const bolt of this.bolts) {
      if (!bolt.active) {
        continue;
      }
      bolt.life -= dt;
      if (bolt.life <= 0) {
        bolt.active = false;
      }
    }
  }

  private spawnBolt(
    minLane: ManiaLane,
    maxLane: ManiaLane,
    canvasWidth: number,
    canvasHeight: number,
  ): void {
    const slot = this.bolts.find((b) => !b.active);
    if (!slot) {
      return;
    }
    const start = getLaneStrumCentre(canvasWidth, canvasHeight, minLane);
    const end = getLaneStrumCentre(canvasWidth, canvasHeight, maxLane);
    slot.startX = start.x;
    slot.endX = end.x;
    slot.y = start.y;
    slot.life = LIGHTNING_BOLT_LIFE_S;
    slot.maxLife = LIGHTNING_BOLT_LIFE_S;
    slot.active = true;
    const amplitude = LIGHTNING_JAG_AMPLITUDE_PX;
    for (let i = 0; i < LIGHTNING_JAG_SEGMENTS; i += 1) {
      slot.jags[i] = (Math.random() * 2 - 1) * amplitude;
    }
  }

  private tracePath(
    ctx: CanvasRenderingContext2D,
    bolt: Bolt,
    baseY: number,
  ): void {
    const dx = (bolt.endX - bolt.startX) / (LIGHTNING_JAG_SEGMENTS + 1);
    ctx.beginPath();
    ctx.moveTo(bolt.startX, baseY);
    for (let i = 0; i < LIGHTNING_JAG_SEGMENTS; i += 1) {
      ctx.lineTo(bolt.startX + dx * (i + 1), baseY + bolt.jags[i]);
    }
    ctx.lineTo(bolt.endX, baseY);
  }
}
